import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { Dictionary, DictionaryItem, DictionaryType } from '../../../../domain/dictionary.model';
import { ValidationError } from '../../../../domain/error.model';
import { HogaRequest, HogaSuggestType } from '../../../../domain/hoga.model';
import { Address } from '../../../../domain/party.domain';
import { ErrorAwareComponent, LsnErrorStateMatcher } from '../../../shared/middleware/error-state-matcher';
import { HogaService } from '../../service/hoga.service';
import { DictionaryState } from '../../store/dictionary.state';
import { ErrorsState } from '../../store/errors.state';

@Component({
    selector: 'app-address-form',
    templateUrl: './address-form.component.html',
    styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent implements OnInit, OnDestroy, ErrorAwareComponent {

    @Input() title: string;
    @Input() address: Address;
    @Input() prefix: string;
    @Input() aside = false;
    @Input() allowForeign: boolean = false;
    @Input() disableApartment = false;

    cities: DictionaryItem[] = [];
    streets: DictionaryItem[] = [];

    constructor(private hogaService: HogaService) {
    }

    @Select(ErrorsState.getMainErrors) mainErrors$: Observable<ValidationError[]>;
    @Select(ErrorsState.getAsideErrors) asideErrors$: Observable<ValidationError[]>;

    errors: ValidationError[] = [];
    subscriptions: Subscription[] = [];

    @Select(DictionaryState.getDictionaries) dictionaries$: Observable<{ [key: string]: Dictionary }>;
    dictionaryTypes = DictionaryType;

    matchers: any = {};
    allowCityInput = true;

    ngOnInit(): void {
        this.subscriptions.push(
            this.aside
                ? this.asideErrors$.subscribe(errors => this.errors = errors)
                : this.mainErrors$.subscribe(errors => this.errors = errors)
        );
        this.prepareMatchers();
        if (this.address.country?.code === 'PL') {
            this.getCities();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    getCities() {
        if (this.validPostalCode(this.address.postalCode)) {
            const request = new HogaRequest(HogaSuggestType.CITY, this.address.postalCode);
            this.hogaService.search(request)
                .subscribe(cities => {
                    this.cities = cities.map(c => new DictionaryItem(c))
                    if (cities.length === 1) {
                        this.setCity(cities[0]);
                    } else if (cities.indexOf(this.address.city) === -1) {
                        this.setCity(null);
                    } else {
                        this.setCity(this.address.city);
                    }
                });
        } else {
            this.setCity(null);
        }
    }

    setCity($event: any) {
        this.address.city = $event;
        if ($event === null) {
            this.streets = [];
            this.address.streetName = null;
            this.allowCityInput = false;
        } else {
            const request = new HogaRequest(HogaSuggestType.STREET, this.address.postalCode, this.address.city);
            this.hogaService.search(request)
                .subscribe(streets => {
                    this.allowCityInput = streets.length === 0;
                    this.streets = streets.map(s => new DictionaryItem(s))
                    if (streets.length === 1) {
                        this.address.streetName = streets[0];
                    } else if (streets.length > 0 && streets.indexOf(this.address.streetName) === -1) {
                        this.address.streetName = null;
                    }
                }, () => this.allowCityInput = false);
        }
    }

    private prepareMatchers() {
        const merge = (field: string) => this.prefix
            ? `${this.prefix}.${field}`
            : field;

        this.matchers = {
            global: new LsnErrorStateMatcher(this, this.prefix),
            country: new LsnErrorStateMatcher(this, `${merge('country')}`),
            postalCode: new LsnErrorStateMatcher(this, `${merge('postalCode')}`),
            city: new LsnErrorStateMatcher(this, `${merge('city')}`),
            streetPrefix: new LsnErrorStateMatcher(this, `${merge('streetPrefix')}`),
            streetName: new LsnErrorStateMatcher(this, `${merge('streetName')}`),
            streetNumber: new LsnErrorStateMatcher(this, `${merge('streetNumber')}`),
            apartmentNumber: new LsnErrorStateMatcher(this, `${merge('apartmentNumber')}`),
        };
    }

    private validPostalCode(postal) {
        return /^\d{2}\-\d{3}$/.test(postal);
    }

    onCountryChange($event: any) {
        if (($event === 'PL' && this.address.country?.code !== 'PL') ||
            ($event !== 'PL' && this.address.country?.code === 'PL')) {
            this.address.postalCode = '';
            this.address.streetName = '';
            this.address.streetNumber = '';
            this.address.streetPrefix = {code: null};
            this.address.apartmentNumber = '';
            this.cities = [];
            this.streets = [];
            this.allowCityInput = false;
        }
        this.address.country = {code: $event};
    }
}
